/**
 * Tiny wrapper around Google Analytics' `gtag()`.
 *
 * Design goals:
 *   • Safe — if `window.gtag` is undefined (Termly blocked the script, or the
 *     user is offline), every call is a silent no-op. Never throws.
 *   • Fire-and-forget — the helper is synchronous and returns nothing.
 *     We never await anything from GA, so it cannot slow the page down.
 *   • Privacy-aware — Termly's `?autoBlock=on` blocker gates the GA
 *     script until the user accepts the analytics category in the cookie
 *     banner. When the script is blocked, `window.gtag` is undefined and
 *     every event silently drops.
 *
 * Convention: event names are snake_case and short enough to fit GA's
 * 40-char limit. Param values are stringified before sending so GA never
 * receives an object reference. Long strings are truncated to 100 chars.
 *
 * NOTE: GA imposes a 25-param limit per event and a 100-char value limit
 * on each `string` param. We pre-truncate to be safe.
 */

const VALUE_LIMIT = 100;

function safeParams(params) {
    if (!params || typeof params !== "object") return undefined;
    const out = {};
    for (const [k, v] of Object.entries(params)) {
        if (v === null || v === undefined) continue;
        if (typeof v === "number" || typeof v === "boolean") {
            out[k] = v;
        } else {
            const s = String(v);
            out[k] = s.length > VALUE_LIMIT ? s.slice(0, VALUE_LIMIT) : s;
        }
    }
    return out;
}

export function trackEvent(name, params) {
    if (typeof window === "undefined") return;
    const gtag = window.gtag;
    if (typeof gtag !== "function") return;
    try {
        const cleaned = safeParams(params);
        if (cleaned) gtag("event", name, cleaned);
        else gtag("event", name);
    } catch {
        // Analytics must never crash the app.
    }
}

/**
 * Convenience helper for the "user entered section X" event.
 * Section names live in /lib/analyticsSections.js so the call sites stay
 * pure of magic strings.
 */
export function trackSectionView(section) {
    trackEvent("section_view", { section });
}
